import React, { Component } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import GetAppIcon from '@material-ui/icons/GetApp';
import JoditEditor from 'jodit-react';
import { saveAs } from 'file-saver';
import { userService } from '_services/user.service';
import { generalAuthTokenHeader } from '__helpers/auth-header';
import { apiPath } from 'api';

const styles = theme => ({
  overlay: {
    position: 'relative',
    height: '100%',
    background: '#fff',
    padding: theme.spacing(2),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
    zIndex: 1,
  },
  header: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: '2px solid #51C1C0',
  },
  editorContainer: {
    flex: 1,
    overflow: 'auto',
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    '& .jodit-container': {
      border: 'none',
      height: '100%',
    },
    '& .jodit-wysiwyg': {
      minHeight: '500px',
      height: 'auto !important',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
  },
  saveButton: {
    background: '#51C1C0',
    color: 'white',
    padding: '8px 24px',
    '&:hover': {
      background: '#3fa3a2',
    },
  },
  cancelButton: {
    background: '#ff80aa',
    color: 'white',
    padding: '8px 24px',
    '&:hover': {
      background: '#ff4d94',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '400px',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#666',
  },
  errorState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#f44336',
  },
});

// Jodit editor configuration
const joditConfig = {
  readonly: false,
  height: 'auto',
  minHeight: 500,

  // Full HTML mode - preserves everything
  useIframeResizer: false,
  useSplitMode: false,
  allowResizeX: false,
  allowResizeY: true,

  // HTML processing - preserve all HTML including custom tags
  processPasteHTML: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: 'insert_clear_html',

  // Allow all HTML tags and attributes
  allowTags: true,

  // Disable sanitization
  safeMode: false,

  // Enable fullpage editing (preserves <html>, <head>, <style>, <body>)
  editHTMLDocumentMode: true,

  // Toolbar configuration
  buttons: [
    'source',
    '|',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    '|',
    'ul',
    'ol',
    'outdent',
    'indent',
    '|',
    'font',
    'fontsize',
    'brush',
    'paragraph',
    '|',
    'table',
    'link',
    'image',
    '|',
    'align',
    'undo',
    'redo',
    '|',
    'hr',
    'eraser',
    'copyformat',
    '|',
    'symbol',
    'fullsize',
    'preview',
  ],

  // Button options
  buttonsMD: [
    'source',
    '|',
    'bold',
    'italic',
    'underline',
    '|',
    'ul',
    'ol',
    '|',
    'table',
    'link',
    '|',
    'align',
    'undo',
    'redo',
  ],

  // Enable table plugin
  table: {
    allowCellSelection: true,
    useExtraClassesOptions: true,
  },

  // Additional options
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  toolbarAdaptive: false,
  toolbarSticky: false,

  // Prevent auto-cleanup of HTML
  cleanHTML: {
    replaceNBSP: false,
    fillEmptyParagraph: false,
    removeEmptyElements: false,
  },
};

class ReferralSummary extends Component {
  constructor(props) {
    super(props);
    let spinner = document.getElementById("loadingSpinner");

    this.state = {
      open: props.open || false,
      patientId: props.patientId,
      loading: true,
      saving: false,
      downloading: false,
      summaryData: null,
      summaryPdfUrl: '',
      patientName: '',
      dateOfBirth: '',
      originalHtml: '',
      fullHtml: '',
      hasUnsavedChanges: false,
      notFound: false,
      error: null,
      spinner: spinner,
    };

    this.editorRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.patientId) {
      this.fetchReferralSummary();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open !== this.props.open && this.props.open) {
      this.fetchReferralSummary();
    }
  }

  /**
   * Fetch referral summary from backend
   */
  fetchReferralSummary = async () => {
    const { patientId } = this.props;

    if (!patientId) {
      this.setState({
        loading: false,
        error: 'Invalid patient ID'
      });
      return;
    }

    this.setState({ loading: true, error: null, notFound: false });

    try {
      const apiUrl = this.props.apiGetUrl
        ? this.props.apiGetUrl
        : `${apiPath.GetReferralSummary}/${patientId}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: generalAuthTokenHeader(),
      });

      if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
        return;
      }

      if (response.status === 404) {
        // No summary found - show empty editor
        this.setState({
          loading: false,
          notFound: true,
          originalHtml: '',
          fullHtml: '',
          summaryData: null,
          summaryPdfUrl: '',
          patientName: '',
          dateOfBirth: '',
        });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch referral summary');
      }

      const data = await response.json();
      const htmlContent = data.summaryHtml || '';

      this.setState({
        loading: false,
        summaryData: data,
        summaryPdfUrl: data.summaryPdfUrl || '',
        patientName: data.patientName || 'N/A',
        dateOfBirth: data.dateOfBirth || 'N/A',
        originalHtml: htmlContent,
        fullHtml: htmlContent,
        hasUnsavedChanges: false,
      });

    } catch (error) {
      console.error('Error fetching referral summary:', error);
      this.setState({
        loading: false,
        error: error.message || 'Failed to load referral summary',
      });

      userService.showNotification({
        title: 'Error',
        message: 'Failed to load referral summary',
        type: 'danger',
      });
    }
  };

  /**
   * Handle save - call PATCH API
   */
  handleSave = async () => {
    const { patientId } = this.props;
    const { fullHtml } = this.state;

    if (!patientId) {
      userService.showNotification({
        title: 'Error',
        message: 'Invalid patient ID',
        type: 'danger',
      });
      return;
    }

    this.setState({ saving: true });
    if (this.state.spinner) {
      this.state.spinner.removeAttribute("hidden");
    }

    try {
      const payload = {
        patientId: patientId,
        summaryHtml: fullHtml,
      };

      const apiUrl = this.props.apiPatchUrl
        ? this.props.apiPatchUrl
        : apiPath.UpdateReferralSummary;
      const response = await fetch(apiUrl, {
        method: 'PATCH',
        headers: {
          ...generalAuthTokenHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
        return;
      }

      if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Bad Request');
      }

      if (!response.ok) {
        throw new Error('Failed to update referral summary');
      }

      const updatedData = await response.json();

      this.setState({
        saving: false,
        summaryData: updatedData,
        summaryPdfUrl: updatedData.summaryPdfUrl || '',
        patientName: updatedData.patientName || 'N/A',
        dateOfBirth: updatedData.dateOfBirth || 'N/A',
        originalHtml: updatedData.summaryHtml,
        fullHtml: updatedData.summaryHtml,
        hasUnsavedChanges: false,
        notFound: false,
      });

      userService.showNotification({
        title: 'Success',
        message: 'Referral summary updated successfully!',
        type: 'success',
      });

      // Optionally close modal after successful save
      // this.handleClose();

    } catch (error) {
      console.error('Error updating referral summary:', error);
      this.setState({ saving: false });

      userService.showNotification({
        title: 'Error',
        message: error.message || 'Failed to update referral summary',
        type: 'danger',
      });
    }

    if (this.state.spinner) {
      this.state.spinner.setAttribute("hidden", "true");
    }
  };

  /**
   * Handle PDF download
   */
  handleDownload = async () => {
    const { summaryPdfUrl } = this.state;
    const { patientId } = this.props;

    if (!summaryPdfUrl) {
      userService.showNotification({
        title: 'Error',
        message: 'No PDF available for download',
        type: 'danger',
      });
      return;
    }

    this.setState({ downloading: true });

    try {
      // Use backend proxy endpoint to download PDF (avoids CORS issues)
      const apiUrl = this.props.apiDownloadUrl
        ? this.props.apiDownloadUrl
        : `${apiPath.DownloadReferralSummaryPdf}/${patientId}/download-pdf`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: generalAuthTokenHeader(),
      });

      if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Convert to blob
      const blob = await response.blob();

      // Generate filename
      const filename = `referral_summary_patient_${patientId}.pdf`;

      // Trigger download using file-saver
      saveAs(blob, filename);

      userService.showNotification({
        title: 'Success',
        message: 'PDF downloaded successfully!',
        type: 'success',
      });

    } catch (error) {
      console.error('Error downloading PDF:', error);
      userService.showNotification({
        title: 'Error',
        message: error.message || 'Failed to download PDF',
        type: 'danger',
      });
    } finally {
      this.setState({ downloading: false });
    }
  };

  /**
   * Handle Jodit editor changes
   */
  handleEditorChange = (newContent) => {
    this.setState({
      fullHtml: newContent,
      hasUnsavedChanges: true,
    });
  };

  /**
   * Handle close with unsaved changes confirmation
   */
  handleClose = () => {
    const { hasUnsavedChanges } = this.state;

    if (hasUnsavedChanges) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmClose) {
        return;
      }
    }

    this.setState({
      hasUnsavedChanges: false,
    });

    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  render() {
    const { classes } = this.props;
    const {
      loading,
      saving,
      downloading,
      fullHtml,
      summaryPdfUrl,
      notFound,
      error,
      hasUnsavedChanges
    } = this.state;

    return (
      <Dialog
        open={this.props.open}
        onClose={this.handleClose}
        maxWidth={false}
        style={{ zIndex: 1000001 }}
        PaperProps={{
          style: {
            width: '95vw',
            maxWidth: '1400px',
            height: '90vh',
            maxHeight: '90vh',
            margin: '5vh auto',
            borderRadius: 10,
          }
        }}
      >
        <DialogContent className={classes.overlay}>
          <IconButton
            className={classes.closeButton}
            onClick={this.handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <Box className={classes.header}>
            <Typography variant="h4" style={{ color: '#51C1C0', fontWeight: 'bold' }}>
              AI Referral Insights
            </Typography>
            {/* <Typography variant="body2" style={{ color: '#666', fontStyle: 'italic', marginTop: 4 }}>
              NOTE: This summary is generated by Artificial Intelligence to assist with care coordination. It is not a comprehensive medical record, a physician's order, or a substitute for professional medical judgment. The Receiving Provider/Care Team is solely responsible for verifying all clinical data—including medications, allergies, and care instructions—against the official discharge documentation prior to initiating care. This software provider assumes no liability for clinical decisions made based on this text.
            </Typography> */}
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Patient ID: {this.props.patientId} &nbsp;&nbsp; Name: {this.state.patientName} &nbsp;&nbsp; Date of Birth: {this.state.dateOfBirth}
              {hasUnsavedChanges && <span style={{ color: '#ff9800', marginLeft: 8 }}>● Unsaved changes</span>}
            </Typography>
          </Box>

          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={60} style={{ color: '#51C1C0' }} />
            </Box>
          ) : error ? (
            <Box className={classes.errorState}>
              <Typography variant="h6">{error}</Typography>
              <Button
                onClick={this.fetchReferralSummary}
                style={{ marginTop: 16, backgroundColor: '#51C1C0', color: 'white' }}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <>
              {notFound && (
                <Box className={classes.emptyState}>
                  <Typography variant="h6" gutterBottom>
                    No referral summary found for this patient.
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Create a new summary using the editor below.
                  </Typography>
                </Box>
              )}

              <Box className={classes.editorContainer}>
                <JoditEditor
                  ref={this.editorRef}
                  value={fullHtml}
                  config={joditConfig}
                  onBlur={this.handleEditorChange}
                  onChange={() => {}} // Use onBlur instead of onChange for better performance
                />
              </Box>

              <Box className={classes.buttonContainer}>
                {!this.props.hideDownloadButton && (
                  <Button
                    variant="outlined"
                    onClick={this.handleDownload}
                    disabled={!summaryPdfUrl || downloading || saving}
                    startIcon={downloading ? <CircularProgress size={20} /> : <GetAppIcon />}
                    style={{
                      marginRight: 'auto',
                      borderColor: '#51C1C0',
                      color: '#51C1C0'
                    }}
                  >
                    {downloading ? 'Downloading...' : 'Download PDF'}
                  </Button>
                )}
                <Button
                  className={classes.cancelButton}
                  onClick={this.handleClose}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  className={classes.saveButton}
                  onClick={this.handleSave}
                  disabled={saving || !hasUnsavedChanges}
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }
}

export default withStyles(styles)(ReferralSummary);
